import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Pencil, Trash2, Tag, Image, X, Plus, Layers, Upload } from "lucide-react";
import { createPortal } from "react-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getAuthHeaders() {
  const token = localStorage.getItem("token");
  return { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
}

// ── Helpers ─────────────────────────────────────────────────────────────────
function toBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// ── Single image upload ──────────────────────────────────────────────────────
function ImageUpload({ value, onChange, label = "Image" }) {
  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    onChange(await toBase64(file));
  };
  return (
    <div>
      <label className="field-label">{label}</label>
      <label className="flex items-center gap-3 cursor-pointer bg-[#F7F6F2] border-2 border-dashed border-black/20 hover:border-[#161616] px-4 py-3 transition-colors">
        <Upload size={16} className="text-[#5A5955] shrink-0" />
        <span className="text-sm text-[#5A5955]">{value ? "Change image" : "Click to upload"}</span>
        <input type="file" accept="image/*" className="hidden" onChange={handleFile} />
      </label>
      {value && (
        <div className="mt-3 relative group">
          <img src={value} alt="Preview" className="w-full h-36 object-cover border border-[#D9D7D0]" />
          <button type="button" onClick={() => onChange("")}
            className="absolute top-1 right-1 bg-black/70 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <X size={12} />
          </button>
        </div>
      )}
    </div>
  );
}

// ── Multiple gallery upload ──────────────────────────────────────────────────
function GalleryUpload({ images, onChange }) {
  const handleFiles = async (e) => {
    const files = Array.from(e.target.files);
    const b64s = await Promise.all(files.map(toBase64));
    onChange([...images, ...b64s]);
    e.target.value = "";
  };
  const remove = (idx) => onChange(images.filter((_, i) => i !== idx));

  return (
    <div className="space-y-3">
      <label className="flex items-center gap-3 cursor-pointer bg-[#F7F6F2] border-2 border-dashed border-black/20 hover:border-[#161616] px-4 py-3 transition-colors">
        <Upload size={16} className="text-[#5A5955] shrink-0" />
        <span className="text-sm text-[#5A5955]">Click to upload images (select multiple)</span>
        <input type="file" accept="image/*" multiple className="hidden" onChange={handleFiles} data-testid="gallery-file-input" />
      </label>
      {images.length > 0 && (
        <div className="grid grid-cols-4 gap-2">
          {images.map((src, i) => (
            <div key={i} className="relative group">
              <img src={src} alt={`gallery-${i}`} className="w-full h-20 object-cover border border-[#D9D7D0]" />
              <button type="button" onClick={() => remove(i)}
                className="absolute top-1 right-1 bg-black/70 text-white p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                <X size={10} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Variant Modal ────────────────────────────────────────────────────────────
const emptyVariantForm = { name: "", description: "", cover_image: "", gallery_images: [], starting_at: "" };

function VariantModal({ editing, onClose, onSaved, categoryId }) {
  const [form, setForm] = useState(
    editing
      ? { name: editing.name, description: editing.description || "", cover_image: editing.cover_image || "", gallery_images: editing.gallery_images || [], starting_at: editing.starting_at ?? "" }
      : emptyVariantForm
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    const payload = { ...form, starting_at: form.starting_at === "" ? 0 : parseFloat(form.starting_at) };
    try {
      if (editing) {
        await axios.put(`${API}/categories/${categoryId}/variants/${editing.id}`, payload, { headers: getAuthHeaders() });
      } else {
        await axios.post(`${API}/categories/${categoryId}/variants`, payload, { headers: getAuthHeaders() });
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong.");
    } finally { setSubmitting(false); }
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="variant-modal">
        <div className="flex items-center justify-between px-6 sm:px-8 py-5 border-b border-[#161616] sticky top-0 bg-white z-10">
          <h2 className="text-xl font-bold tracking-tight text-[#161616]">{editing ? "Edit Variant" : "Add Variant"}</h2>
          <button onClick={onClose} className="p-1 hover:bg-[#F4F3EF]"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          <div>
            <label className="field-label">Name *</label>
            <input type="text" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="variant-input-name"
              className="field"
              placeholder="Variant name" />
          </div>

          <div>
            <label className="field-label">Description</label>
            <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              data-testid="variant-input-description"
              className="field resize-y"
              placeholder="Brief description..." />
          </div>

          <ImageUpload
            label="Cover Image"
            value={form.cover_image}
            onChange={(b64) => setForm({ ...form, cover_image: b64 })}
          />

          <div>
            <label className="field-label">Gallery Images</label>
            <GalleryUpload
              images={form.gallery_images}
              onChange={(imgs) => setForm({ ...form, gallery_images: imgs })}
            />
          </div>

          <div>
            <label className="field-label">Starting At (&#8377;)</label>
            <input type="number" min="0" step="0.01" value={form.starting_at} onChange={(e) => setForm({ ...form, starting_at: e.target.value })}
              data-testid="variant-input-price"
              className="field"
              placeholder="e.g. 299" />
          </div>

          {error && <p className="text-sm text-red-600 bg-red-50 px-4 py-2 border border-red-200">{error}</p>}

          <button type="submit" disabled={submitting} data-testid="submit-variant-btn"
            className="w-full bg-[#161616] text-white font-bold px-8 py-4 text-sm hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_0px_#E5007D] transition-all duration-200 disabled:opacity-50">
            {submitting ? "Saving..." : editing ? "Update Variant" : "Add Variant"}
          </button>
        </form>
      </div>
    </div>,
    document.body
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────
export default function CategoryDetailPage() {
  const { categoryId } = useParams();
  const navigate = useNavigate();

  const [category, setCategory] = useState(null);
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showCatEdit, setShowCatEdit] = useState(false);
  const [catForm, setCatForm] = useState({ name: "", description: "", image: "", starting_price: "" });
  const [catSubmitting, setCatSubmitting] = useState(false);
  const [catError, setCatError] = useState("");

  const [showVariant, setShowVariant] = useState(false);
  const [editingVariant, setEditingVariant] = useState(null);

  useEffect(() => { fetchAll(); }, [categoryId]);

  const fetchAll = async () => {
    try {
      const [catRes, varRes] = await Promise.all([
        axios.get(`${API}/categories/${categoryId}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/categories/${categoryId}/variants`, { headers: getAuthHeaders() }),
      ]);
      setCategory(catRes.data);
      setVariants(varRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const openCatEdit = () => {
    setCatForm({ name: category.name, description: category.description || "", image: category.image || "", starting_price: category.starting_price ?? "" });
    setCatError("");
    setShowCatEdit(true);
  };

  const handleCatSubmit = async (e) => {
    e.preventDefault();
    setCatSubmitting(true);
    setCatError("");
    const payload = { ...catForm, starting_price: catForm.starting_price === "" ? 0 : parseFloat(catForm.starting_price) };
    try {
      const { data } = await axios.put(`${API}/categories/${categoryId}`, payload, { headers: getAuthHeaders() });
      setCategory(data);
      setShowCatEdit(false);
    } catch (err) {
      setCatError(err.response?.data?.detail || "Something went wrong.");
    } finally { setCatSubmitting(false); }
  };

  const handleCatDelete = async () => {
    if (!window.confirm("Delete this category and all its variants? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/categories/${categoryId}`, { headers: getAuthHeaders() });
      navigate("/admin/master/categories");
    } catch (err) { console.error(err); }
  };

  const deleteVariant = async (variantId) => {
    if (!window.confirm("Delete this variant?")) return;
    try {
      await axios.delete(`${API}/categories/${categoryId}/variants/${variantId}`, { headers: getAuthHeaders() });
      setVariants((prev) => prev.filter((v) => v.id !== variantId));
    } catch (err) { console.error(err); }
  };

  if (loading) {
    return (
      <div data-testid="category-detail-loading" className="min-h-screen bg-[#F4F3EF] flex items-center justify-center">
        <p className="text-[#5A5955]">Loading...</p>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="min-h-screen bg-[#F4F3EF] flex items-center justify-center">
        <div className="text-center">
          <Tag size={40} className="mx-auto text-[#D9D7D0] mb-4" />
          <p className="text-[#161616] font-bold text-lg">Category not found</p>
          <button onClick={() => navigate("/admin/master/categories")}
            className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-[#161616] hover:text-[#E5007D] transition-colors">
            <ArrowLeft size={14} /> Back to Categories
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="category-detail-page" className="min-h-screen bg-[#F4F3EF]">
      {/* Header */}
      <div className="bg-white border-b border-[#161616] px-5 sm:px-8 py-6 gap-4 flex-wrap flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/admin/master/categories")} data-testid="back-to-categories"
            className="p-2 hover:bg-[#F4F3EF] transition-colors">
            <ArrowLeft size={18} className="text-[#5A5955]" />
          </button>
          <div>
            <p className="text-xs font-bold text-[#5A5955] mb-1">Master Data / Categories</p>
            <h1 className="text-3xl font-bold tracking-[-0.02em] text-[#161616]">{category.name}</h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={openCatEdit} data-testid="edit-category-btn"
            className="inline-flex items-center gap-2 bg-white border border-[#D9D7D0] text-[#161616] font-bold px-4 py-2.5 text-sm hover:bg-[#F4F3EF] transition-colors">
            <Pencil size={14} /> Edit
          </button>
          <button onClick={handleCatDelete} data-testid="delete-category-btn"
            className="inline-flex items-center gap-2 bg-red-50 border border-red-200 text-red-600 font-bold px-4 py-2.5 text-sm hover:bg-red-100 transition-colors">
            <Trash2 size={14} /> Delete
          </button>
        </div>
      </div>

      <div className="px-8 py-8 space-y-8 max-w-4xl">
        {/* Category Info */}
        <div className="bg-white border border-[#D9D7D0] overflow-hidden">
          {category.image ? (
            <img src={category.image} alt={category.name} className="w-full h-56 object-cover" />
          ) : (
            <div className="w-full h-56 bg-[#F4F3EF] flex flex-col items-center justify-center gap-3">
              <Image size={40} className="text-[#D9D7D0]" />
              <p className="text-xs text-[#5A5955]">No image added</p>
            </div>
          )}
          <div className="p-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
              <div>
                <p className="text-xs font-bold text-[#5A5955] mb-1">Name</p>
                <p className="text-base font-bold text-[#161616]">{category.name}</p>
              </div>
              <div>
                <p className="text-xs font-bold text-[#5A5955] mb-1">Starting Price</p>
                <p className="text-base font-black text-[#161616]">{category.starting_price > 0 ? `₹${category.starting_price}` : "—"}</p>
              </div>
            </div>
            <div className="mb-6">
              <p className="text-xs font-bold text-[#5A5955] mb-1">Description</p>
              <p className="text-sm text-[#5A5955] leading-relaxed">{category.description || "No description provided."}</p>
            </div>
            <div>
              <p className="text-xs font-bold text-[#5A5955] mb-1">Created</p>
              <p className="text-sm text-[#5A5955]">
                {category.created_at ? new Date(category.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }) : "—"}
              </p>
            </div>
          </div>
        </div>

        {/* Variants Section */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Layers size={18} className="text-[#5A5955]" />
              <h2 className="text-lg font-bold text-[#161616]">Variants</h2>
              <span className="text-xs font-bold bg-[#F4F3EF] text-[#5A5955] px-2 py-0.5">{variants.length}</span>
            </div>
            <button onClick={() => { setEditingVariant(null); setShowVariant(true); }}
              data-testid="add-variant-btn"
              className="inline-flex items-center gap-2 bg-[#161616] text-white font-bold px-4 py-2.5 text-sm hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_0px_#E5007D] transition-all duration-200">
              <Plus size={14} /> Add Variant
            </button>
          </div>

          {variants.length === 0 ? (
            <div className="bg-white border border-dashed border-[#D9D7D0] p-10 text-center">
              <Layers size={32} className="mx-auto text-[#D9D7D0] mb-3" />
              <p className="text-sm text-[#5A5955]">No variants yet. Add one to get started.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {variants.map((v) => (
                <div key={v.id} data-testid={`variant-card-${v.id}`}
                  className="bg-white border border-[#D9D7D0] overflow-hidden group hover:shadow-md transition-shadow">
                  {v.cover_image ? (
                    <img src={v.cover_image} alt={v.name} className="w-full h-36 object-cover group-hover:scale-105 transition-transform duration-300" />
                  ) : (
                    <div className="w-full h-36 bg-[#F4F3EF] flex items-center justify-center">
                      <Image size={24} className="text-[#D9D7D0]" />
                    </div>
                  )}
                  <div className="p-4 space-y-2">
                    <h3 className="text-sm font-bold text-[#161616]">{v.name}</h3>
                    {v.description && <p className="text-xs text-[#5A5955] line-clamp-2 leading-relaxed">{v.description}</p>}
                    {v.starting_at > 0 && (
                      <p className="text-sm font-black text-[#161616]">
                        &#8377;{v.starting_at} <span className="text-xs font-medium text-[#5A5955]">onwards</span>
                      </p>
                    )}
                    {v.gallery_images?.length > 0 && (
                      <div className="flex gap-1 pt-1">
                        {v.gallery_images.slice(0, 4).map((img, i) => (
                          <img key={i} src={img} alt="" className="w-8 h-8 object-cover border border-[#D9D7D0]" />
                        ))}
                        {v.gallery_images.length > 4 && (
                          <div className="w-8 h-8 bg-[#F4F3EF] border border-[#D9D7D0] flex items-center justify-center">
                            <span className="text-xs font-bold text-[#5A5955]">+{v.gallery_images.length - 4}</span>
                          </div>
                        )}
                      </div>
                    )}
                    <div className="flex items-center gap-1 pt-2 border-t border-[#D9D7D0]">
                      <button onClick={() => { setEditingVariant(v); setShowVariant(true); }}
                        data-testid={`edit-variant-${v.id}`}
                        className="p-2 hover:bg-[#F4F3EF] transition-colors" title="Edit">
                        <Pencil size={13} className="text-[#5A5955]" />
                      </button>
                      <button onClick={() => deleteVariant(v.id)}
                        data-testid={`delete-variant-${v.id}`}
                        className="p-2 hover:bg-red-50 transition-colors" title="Delete">
                        <Trash2 size={13} className="text-red-500" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Category Edit Modal */}
      {showCatEdit && createPortal(
        <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4" onClick={() => setShowCatEdit(false)}>
          <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="edit-category-modal">
            <div className="flex items-center justify-between px-6 sm:px-8 py-5 border-b border-[#161616]">
              <h2 className="text-xl font-bold tracking-tight text-[#161616]">Edit Category</h2>
              <button onClick={() => setShowCatEdit(false)} className="p-1 hover:bg-[#F4F3EF]"><X size={20} /></button>
            </div>
            <form onSubmit={handleCatSubmit} className="p-8 space-y-5">
              <div>
                <label className="field-label">Name *</label>
                <input type="text" required value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })}
                  data-testid="edit-cat-name"
                  className="field" placeholder="Category name" />
              </div>
              <div>
                <label className="field-label">Description</label>
                <textarea rows={3} value={catForm.description} onChange={(e) => setCatForm({ ...catForm, description: e.target.value })}
                  data-testid="edit-cat-description"
                  className="field resize-y" placeholder="Brief description..." />
              </div>
              <ImageUpload
                label="Image"
                value={catForm.image}
                onChange={(b64) => setCatForm({ ...catForm, image: b64 })}
              />
              <div>
                <label className="field-label">Starting Price (&#8377;)</label>
                <input type="number" min="0" step="0.01" value={catForm.starting_price} onChange={(e) => setCatForm({ ...catForm, starting_price: e.target.value })}
                  data-testid="edit-cat-price"
                  className="field" placeholder="e.g. 499" />
              </div>
              {catError && <p className="text-sm text-red-600 bg-red-50 px-4 py-2 border border-red-200">{catError}</p>}
              <button type="submit" disabled={catSubmitting} data-testid="submit-edit-category-btn"
                className="w-full bg-[#161616] text-white font-bold px-8 py-4 text-sm hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_0px_#E5007D] transition-all duration-200 disabled:opacity-50">
                {catSubmitting ? "Saving..." : "Update Category"}
              </button>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Variant Modal */}
      {showVariant && (
        <VariantModal
          editing={editingVariant}
          categoryId={categoryId}
          onClose={() => { setShowVariant(false); setEditingVariant(null); }}
          onSaved={fetchAll}
        />
      )}
    </div>
  );
}
